import { useCallback } from "react";
import {
  Autocomplete,
  Box,
  Fade,
  FormControl,
  Typography,
} from "@mui/material";
import { Card } from "@avenue-8/sidekick-ui";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  StartMlsSubscriptionFlowSchema,
  startMlsSubscriptionFlowFormSchema,
} from "./start-mls-subscription-flow-helpers";
import { REGISTRATION_OPTION_STORAGE_KEY } from "@pages/registration/subscription-registration-helpers";
import { TextField } from "@shared/components/TextField";
import { createAutocompleteFilterOptionsFunction } from "@shared/utils/autocomplete-extentions";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { SubscriptionSubmitButton } from "../components/styles";
import { StartSsoMlsSubscription } from "./starters/StartSsoMlsSubscription";
import { StartCredentialBasedMlsSubscription } from "./starters/StartCredentialBasedMlsSubscription";
import { useGetMembershipAffiliations } from "@queries/subscription-flow/useGetMembershipAffiliations";
import { MembershipAffiliationDto } from "@shared/apis/sidekick/generated";
import { isMlsSsoLoginOption } from "@features/Auth/login-options-form-helpers";
import { JOIN_SIDEKICK_INTEREST_LIST_URL } from "@shared/utils/constants";

type Props = {
  defaultValues: {
    partnerId?: string;
  };
};

const filterOptions =
  createAutocompleteFilterOptionsFunction<MembershipAffiliationDto>([
    "name",
    "abbreviation",
  ]);

export const StartMlsSubscriptionForm = (props: Props) => {
  const { partnerId } = props.defaultValues;
  const navigate = useNavigate();
  const { data: affiliations = [] } = useGetMembershipAffiliations();

  const acceptingAffiliations = affiliations.filter(
    (a) => a.acceptingNewSubscribers && a.id !== "independent",
  );

  const {
    formState: { errors },
    control,
    getValues,
    trigger,
  } = useForm<StartMlsSubscriptionFlowSchema>({
    defaultValues: {
      partner: partnerId ?? "",
    },
    resolver: yupResolver(startMlsSubscriptionFlowFormSchema),
    reValidateMode: "onChange",
  });

  const onNextClick = useCallback(async () => {
    const isValidPartner = await trigger();
    if (!isValidPartner) return;
    const selectedPartner = getValues("partner");
    sessionStorage.setItem(REGISTRATION_OPTION_STORAGE_KEY, selectedPartner);
    navigate(`/subscribe/partner/${selectedPartner}`);
  }, [trigger, getValues, navigate]);

  const selectedAffiliation = partnerId
    ? (affiliations.find((a) => a.id === partnerId) ?? null)
    : null;

  return (
    <div>
      {!partnerId && (
        <Box>
          <Typography mb={2} variant="h5">
            Find your MLS or brokerage
          </Typography>

          <FormControl fullWidth>
            <Controller
              name="partner"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  options={acceptingAffiliations}
                  getOptionLabel={(option) => option.name}
                  filterOptions={filterOptions}
                  onChange={(_, value) => {
                    field.onChange(value?.id || "");
                    trigger("partner");
                  }}
                  disableClearable
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select your MLS or brokerage"
                      fieldError={errors.partner}
                    />
                  )}
                />
              )}
            />
          </FormControl>

          <Box mt={1}>
            <SubscriptionSubmitButton
              onClick={onNextClick}
              type="button"
              fullWidth
            >
              Continue through my organization
            </SubscriptionSubmitButton>
          </Box>

          <Box mt={4}>
            <Card sx={{ p: 2 }}>
              <Typography color={"secondary"} mb={1}>
                Can&apos;t find your organization?
              </Typography>
              <Typography variant="body2">
                <a
                  href={JOIN_SIDEKICK_INTEREST_LIST_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Join the interest list
                </a>
              </Typography>
            </Card>
          </Box>

          <Typography textAlign="center" mt={6}>
            Already have an account? <Link to="/login">Sign In</Link>
          </Typography>
        </Box>
      )}

      {!!partnerId && (
        <Fade in={!!partnerId} timeout={500} unmountOnExit>
          <div>
            <Typography variant="body1" mb={3}>
              Enter your information to unlock a fast, efficient, AI-enhanced
              resource designed to streamline your real estate business.
              Let&lsquo;s get started!
            </Typography>
          </div>
        </Fade>
      )}

      {!!partnerId && !!selectedAffiliation && (
        <Fade in={!!partnerId} timeout={1000} unmountOnExit>
          <Box>
            <MlsForm affiliation={selectedAffiliation} />
          </Box>
        </Fade>
      )}
    </div>
  );
};

const MlsForm = ({
  affiliation,
}: {
  affiliation: MembershipAffiliationDto;
}) => {
  const planId = affiliation.id;

  if (isMlsSsoLoginOption(affiliation))
    return (
      <StartSsoMlsSubscription planId={planId} planName={affiliation.name} />
    );

  return (
    <StartCredentialBasedMlsSubscription
      planId={planId}
      planName={affiliation.name}
      stepStartChallenge={affiliation.subscriptionFlowConfig.stepStartChallenge}
    />
  );
};
